import React from 'react';
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Paper
} from '@mui/material';
import {
  Settings,
  Wifi,
  Sync,
  PowerSettingsNew,
  Edit,
  AssignmentReturn,
  Delete,
  Add,
  Info,
  SwapHoriz
} from '@mui/icons-material';

interface ContextMenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  divider?: boolean;
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
}

interface ContextMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  items: ContextMenuItem[];
  mouseX?: number;
  mouseY?: number;
}

export const ContextMenu: React.FC<ContextMenuProps> = ({
  anchorEl,
  open,
  onClose,
  items,
  mouseX = 0,
  mouseY = 0
}) => {
  const handleItemClick = (item: ContextMenuItem) => {
    if (!item.disabled) {
      item.onClick();
      onClose();
    }
  };

  // Se temos coordenadas do mouse, usar posicionamento absoluto direto
  if (mouseX > 0 && mouseY > 0) {
    return (
      <>
        {open && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1299,
              background: 'transparent'
            }}
            onClick={onClose}
          />
        )}
        {open && (
          <div
            style={{
              position: 'fixed',
              top: mouseY + 5,  // 5px de offset para baixo
              left: mouseX + 5, // 5px de offset para direita
              zIndex: 1300,
              minWidth: 200,
              background: 'white',
              borderRadius: '4px',
              boxShadow: '0 8px 16px rgba(0,0,0,0.15)',
              border: '1px solid rgba(0,0,0,0.12)'
            }}
          >
            {items.map((item, index) => (
              <React.Fragment key={item.id}>
                <div
                  onClick={() => handleItemClick(item)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '8px 16px',
                    cursor: item.disabled ? 'not-allowed' : 'pointer',
                    color: item.color === 'error' ? '#d32f2f' : 
                           item.color === 'warning' ? '#ed6c02' :
                           item.color === 'success' ? '#2e7d32' :
                           item.color === 'info' ? '#0288d1' :
                           item.color === 'secondary' ? '#9c27b0' :
                           item.color === 'primary' ? '#1976d2' : '#000',
                    opacity: item.disabled ? 0.5 : 1,
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    minHeight: '40px',
                    boxSizing: 'border-box'
                  }}
                  onMouseEnter={(e) => {
                    if (!item.disabled) {
                      e.currentTarget.style.backgroundColor = 
                        item.color === 'error' ? '#ffebee' :
                        item.color === 'warning' ? '#fff3e0' :
                        item.color === 'success' ? '#e8f5e8' :
                        item.color === 'info' ? '#e3f2fd' :
                        item.color === 'secondary' ? '#f3e5f5' :
                        item.color === 'primary' ? '#e3f2fd' : '#f5f5f5';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <div style={{ 
                    marginRight: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    width: '24px',
                    height: '24px'
                  }}>
                    {item.icon}
                  </div>
                  <span>{item.label}</span>
                </div>
                {item.divider && index < items.length - 1 && (
                  <div style={{
                    height: '1px',
                    backgroundColor: '#e0e0e0',
                    margin: '4px 0'
                  }} />
                )}
              </React.Fragment>
            ))}
          </div>
        )}
      </>
    );
  }

  // Fallback para o menu Material-UI padrão
  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      PaperProps={{
        elevation: 8,
        sx: {
          minWidth: 200,
          borderRadius: 1,
          '& .MuiMenuItem-root': {
            py: 1,
            px: 2,
          }
        }
      }}
    >
      {items.map((item, index) => (
        <React.Fragment key={item.id}>
          <MenuItem
            onClick={() => handleItemClick(item)}
            disabled={item.disabled}
            sx={{
              color: item.color === 'error' ? 'error.main' : 
                     item.color === 'warning' ? 'warning.main' :
                     item.color === 'success' ? 'success.main' :
                     item.color === 'info' ? 'info.main' :
                     item.color === 'secondary' ? 'secondary.main' : 'inherit',
              '&:hover': {
                backgroundColor: item.color === 'error' ? 'error.light' :
                               item.color === 'warning' ? 'warning.light' :
                               item.color === 'success' ? 'success.light' :
                               item.color === 'info' ? 'info.light' :
                               item.color === 'secondary' ? 'secondary.light' : 'action.hover',
                color: item.color === 'error' ? 'error.contrastText' :
                       item.color === 'warning' ? 'warning.contrastText' :
                       item.color === 'success' ? 'success.contrastText' :
                       item.color === 'info' ? 'info.contrastText' :
                       item.color === 'secondary' ? 'secondary.contrastText' : 'inherit',
              }
            }}
          >
            <ListItemIcon sx={{ 
              color: 'inherit',
              minWidth: 36
            }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText 
              primary={item.label}
              primaryTypographyProps={{
                fontSize: '0.875rem',
                fontWeight: 500
              }}
            />
          </MenuItem>
          {item.divider && index < items.length - 1 && (
            <Divider sx={{ my: 0.5 }} />
          )}
        </React.Fragment>
      ))}
    </Menu>
  );
};

// Componente específico para equipamentos
interface EquipmentContextMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  equipment: any;
  mouseX?: number;
  mouseY?: number;
  onProvisionar?: (equipmentId: number) => void;
  onDesprovisionar?: (equipmentId: number) => void;
  onChecarSinal?: (equipmentId: number) => void;
  onDiagnosticarONU?: (equipmentId: number) => void;
  onReiniciar?: (equipmentId: number) => void;
  onEdit?: (equipment: any) => void;
  onDevolver?: (equipment: any) => void;
  onTransferir?: (equipment: any) => void;
  onVincularCircuito?: (equipmentId: number) => void;
  onDesvincularCircuito?: (equipmentId: number) => void;
  onTrocarCto?: (equipment: any) => void;
  onTrocarEquipamento?: (equipment: any) => void;
}

export const EquipmentContextMenu: React.FC<EquipmentContextMenuProps> = ({
  anchorEl,
  open,
  onClose,
  equipment,
  mouseX,
  mouseY,
  onProvisionar,
  onDesprovisionar,
  onChecarSinal,
  onDiagnosticarONU,
  onReiniciar,
  onEdit,
  onDevolver,
  onTransferir,
  onVincularCircuito,
  onDesvincularCircuito,
  onTrocarCto,
  onTrocarEquipamento
}) => {
  if (!equipment) return null;

  const isProvisioned = equipment.provision_status === 'provisioned';
  const isONU = equipment.modelo?.tipo === 'ONU' || equipment.modelo?.tipo === 'ONT';
  const hasCircuito = !!equipment.circuito_id;
  const isEmUso = equipment?.status_uso?.toLowerCase() === 'em_uso';

  const menuItems: ContextMenuItem[] = [
    // Provisionar - apenas para ONUs não provisionadas
    ...(isONU && onProvisionar && !isProvisioned ? [{
      id: 'provisionar',
      label: 'Provisionar',
      icon: <Settings fontSize="small" />,
      onClick: () => onProvisionar(equipment.id),
      color: 'primary' as const
    }] : []),
    
    // Desprovisionar - apenas para ONUs provisionadas
    ...(isONU && onDesprovisionar && isProvisioned ? [{
      id: 'desprovisionar',
      label: 'Desprovisionar',
      icon: <Settings fontSize="small" />,
      onClick: () => onDesprovisionar(equipment.id),
      color: 'error' as const
    }] : []),

    // Diagnosticar ONU
    ...(isONU && onDiagnosticarONU ? [{
      id: 'diagnosticar-onu',
      label: 'Diagnosticar ONU',
      icon: <Info fontSize="small" />,
      onClick: () => onDiagnosticarONU(equipment.id),
    }] : []),
    
    // Checar Sinal
    ...(isONU && isProvisioned &&onChecarSinal ? [{
      id: 'checar-sinal',
      label: 'Checar Sinal',
      icon: <Wifi fontSize="small" />,
      onClick: () => onChecarSinal(equipment.id)
    }] : []),     
    
    // Reiniciar
    ...(isONU && isProvisioned && onReiniciar ? [{
      id: 'reiniciar',
      label: 'Reiniciar',
      icon: <PowerSettingsNew fontSize="small" />,
      onClick: () => onReiniciar(equipment.id),
      color: 'warning' as const
    }] : []),

    // Trocar de CTO - ONUs vinculadas a uma caixa
    ...(isONU && onTrocarCto && equipment.caixa_id ? [{
      id: 'trocar-cto',
      label: 'Trocar de CTO',
      icon: <SwapHoriz fontSize="small" />,
      onClick: () => onTrocarCto(equipment),
      color: 'primary' as const
    }] : []),

    // Trocar Equipamento
    ...(onTrocarEquipamento ? [{
      id: 'trocar-equipamento',
      label: 'Trocar Equipamento',
      icon: <SwapHoriz fontSize="small" />,
      onClick: () => onTrocarEquipamento(equipment),
      disabled: isONU && isProvisioned,
      color: 'primary' as const
    }] : []),

    // Vincular Circuito
    ...(isONU && !equipment?.circuito_id && onVincularCircuito ? [{
      id: 'vincular-circuito',
      label: 'Vincular Circuito',
      icon: <Wifi fontSize="small" />,
      onClick: () => onVincularCircuito(equipment.id),
      color: 'info' as const
    }] : []),

    // Desvincular Circuito
    ...(isONU && equipment?.circuito_id && onDesvincularCircuito ? [{
      id: 'desvincular-circuito',
      label: 'Desvincular Circuito',
      icon: <Wifi fontSize="small" />,
      onClick: () => onDesvincularCircuito(equipment.id),
      color: 'error' as const
    }] : []),
    
    // Divider antes das ações de edição
    ...(onEdit || onDevolver ? [{
      id: 'divider-1',
      label: '',
      icon: <></>,
      onClick: () => {},
      divider: true
    }] : []),
    
    // Editar
    ...(onEdit && !isProvisioned ? [{
      id: 'editar',
      label: 'Editar',
      icon: <Edit fontSize="small" />,
      onClick: () => onEdit(equipment)
    }] : []),
    
    // Transferir
    ...(onTransferir ? [{
      id: 'transferir',
      label: 'Transferir para outro contrato',
      icon: <SwapHoriz fontSize="small" />,
      onClick: () => onTransferir(equipment),
      color: 'info' as const
    }] : []),
    
    // Devolver
    ...(onDevolver && !isProvisioned ? [{
      id: 'devolver',
      label: 'Devolver',
      icon: <Delete fontSize="small" />,
      onClick: () => onDevolver(equipment),
      color: 'error' as const
    }] : [])
  ];

  return (
    <ContextMenu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      items={menuItems}
      mouseX={mouseX}
      mouseY={mouseY}
    />
  );
}; 